/**
 * Reusable login prompt component.
 *
 * Displays sign-in options (passkey primary, with social login buttons when available).
 * Embedded in the profile page for guest users, not a standalone page.
 */

import { LitElement, html, css } from 'lit'
import { customElement } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import { authConfig } from '../../services/auth/auth-config.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/icon/icon.js'

@customElement('auth-login-prompt')
export class AuthLoginPrompt extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: var(--cairn-spacing-xl, 32px);
    }

    .login-container {
      max-width: 400px;
      width: 100%;
      text-align: center;
    }

    .logo {
      width: 96px;
      height: 96px;
      margin: 0 auto var(--cairn-spacing-lg, 24px);
    }

    .logo img {
      width: 100%;
      height: 100%;
    }

    h2 {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px);
    }

    .subtitle {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 var(--cairn-spacing-xl, 32px);
      line-height: var(--cairn-line-height, 1.5);
    }

    .benefits {
      text-align: left;
      margin: 0 0 var(--cairn-spacing-xl, 32px);
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border-radius: var(--cairn-border-radius-lg, 12px);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .benefits h3 {
      font-size: var(--cairn-text-size-sm, 14px);
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .benefit-item {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      padding: var(--cairn-spacing-xs, 4px) 0;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .benefit-item md-icon {
      font-size: 18px;
      color: var(--cairn-success-color, #059669);
      --md-icon-size: 18px;
    }

    .login-actions {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
    }

    md-filled-button,
    md-outlined-button {
      width: 100%;
      --md-filled-button-container-height: 48px;
      --md-filled-button-container-shape: var(--cairn-border-radius, 8px);
      --md-outlined-button-container-height: 48px;
      --md-outlined-button-container-shape: var(--cairn-border-radius, 8px);
    }

    .guest-note {
      margin-top: var(--cairn-spacing-lg, 24px);
      font-size: var(--cairn-text-size-xs, 12px);
      color: var(--cairn-text-muted-color, #94a3b8);
      line-height: var(--cairn-line-height, 1.5);
    }

    .not-configured {
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-background-color-alt, #f1f5f9);
      border-radius: var(--cairn-border-radius, 8px);
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
    }
  `

  private handleLogin(): void {
    void authService.login()
  }

  private handleRegister(): void {
    void import('../../utils/router.js').then(({ router }) => {
      router.navigate('/auth/register')
    })
  }

  override render(): unknown {
    if (!authConfig.isConfigured) {
      return html`
        <div class="login-container">
          <div class="not-configured">
            Authentication is not configured. Set VITE_COGNITO_* environment variables to enable
            sign-in.
          </div>
        </div>
      `
    }

    return html`
      <div class="login-container">
        <div class="logo">
          <img src="/images/cairn-icon-clean.png" alt="Taselith" />
        </div>
        <h2>Sign in to Taselith</h2>
        <p class="subtitle">
          Create an account to sync your data across devices and unlock collaboration features.
        </p>

        <div class="benefits">
          <h3>With an account you can</h3>
          <div class="benefit-item">
            <md-icon>sync</md-icon>
            <span>Sync trips, routes, and plans across devices</span>
          </div>
          <div class="benefit-item">
            <md-icon>group</md-icon>
            <span>Collaborate with your team in real-time</span>
          </div>
          <div class="benefit-item">
            <md-icon>cloud_upload</md-icon>
            <span>Back up your data to the cloud</span>
          </div>
          <div class="benefit-item">
            <md-icon>fingerprint</md-icon>
            <span>Use passkeys for fast, secure login</span>
          </div>
        </div>

        <div class="login-actions">
          <md-filled-button @click=${this.handleLogin}> Sign in </md-filled-button>
          <md-outlined-button @click=${this.handleRegister}> Create account </md-outlined-button>
        </div>

        <p class="guest-note">
          You can continue using Taselith without an account. Your data is stored locally on this
          device.
        </p>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'auth-login-prompt': AuthLoginPrompt
  }
}
